import React, { useState, useMemo } from "react";
import { Box, Drawer, SxProps, Theme } from "@mui/material";
import { JsonSchemaRoot, JsonSchema } from "./types";
import { SchemaHierarchyTree } from "./SchemaHierarchyTree";
import { SchemaDetailEditor } from "./SchemaDetailEditor";
import { sxClsx } from "utils/styleClsx";
import { DEFAULT_SCHEMA, SchemaPath } from "./constants";

interface JsonSchemaEditorProps {
    defaultValue?: JsonSchemaRoot;
    onChange?: (schema: JsonSchemaRoot) => void;
    sx?: SxProps<Theme>;
    /** When true, existing fields from defaultValue cannot be modified/deleted, only new fields can be edited */
    addOnly?: boolean;
}

/**
 * Recursively collects all paths that exist in a schema
 */
function collectSchemaPaths(
    schema: JsonSchema,
    currentPath: SchemaPath = [],
): Set<string> {
    const paths = new Set<string>();
    paths.add(JSON.stringify(currentPath));

    if (schema.type === "object" && schema.properties) {
        Object.keys(schema.properties).forEach((propName) => {
            const propPath = [...currentPath, propName];
            const childPaths = collectSchemaPaths(
                schema.properties![propName],
                propPath,
            );
            childPaths.forEach((p) => paths.add(p));
        });
    }

    if (schema.type === "array" && schema.items) {
        const itemPath = [...currentPath, "items"];
        const childPaths = collectSchemaPaths(schema.items, itemPath);
        childPaths.forEach((p) => paths.add(p));
    }

    return paths;
}

/**
 * JsonSchemaEditor - two-pane interface for JSON Schema editing
 * Left pane: Hierarchy tree of schema structure
 * Right pane: Detailed editor for selected schema node
 */
const JsonSchemaEditor: React.FC<JsonSchemaEditorProps> = ({
    defaultValue,
    onChange,
    addOnly = false,
    ...props
}) => {
    const [schema, setSchema] = useState<JsonSchemaRoot>(
        defaultValue ?? DEFAULT_SCHEMA,
    );

    const [selectedPath, setSelectedPath] = useState<SchemaPath>([]);
    const [addPropertyPath, setAddPropertyPath] = useState<SchemaPath | null>(
        null,
    );
    const [addItemPath, setAddItemPath] = useState<SchemaPath | null>(null);

    // Track original paths from defaultValue for addOnly mode
    const originalPaths = useMemo(() => {
        if (!addOnly) return new Set<string>();
        const initialSchema = defaultValue ?? DEFAULT_SCHEMA;
        return collectSchemaPaths(initialSchema);
    }, [addOnly, defaultValue]);

    const isPathLocked = (path: SchemaPath): boolean => {
        if (!addOnly) return false;
        return originalPaths.has(JSON.stringify(path));
    };

    const handleSchemaUpdate = (updatedSchema: JsonSchemaRoot) => {
        setSchema(updatedSchema);
        onChange?.(updatedSchema);
    };

    return (
        // The two panes below need 1200px to stay usable, which is wider than
        // some containers. Scroll that width inside this wrapper rather than
        // letting the panes overflow and paint outside their container.
        <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Box
                sx={sxClsx(
                    {
                        display: "flex",
                        height: "600px",
                        overflow: "hidden",
                        minWidth: "1200px",
                    },
                    props.sx,
                )}
            >
                {/* Left Pane - Schema Hierarchy */}
                <Drawer
                    variant="permanent"
                    sx={{
                        width: 300,
                        flexShrink: 0,
                        zIndex: 10,
                        "& .MuiDrawer-paper": {
                            width: 300,
                            maxHeight: "100%",
                            boxSizing: "border-box",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            overflow: "auto",
                            position: "relative",
                        },
                    }}
                >
                    <SchemaHierarchyTree
                        schema={schema}
                        selectedPath={selectedPath}
                        onSelectPath={setSelectedPath}
                        onAddProperty={setAddPropertyPath}
                        onAddItem={setAddItemPath}
                    />
                </Drawer>

                {/* Right Pane - Detail Editor */}
                <Box
                    sx={{
                        flexGrow: 1,
                        width: 0,
                        maxHeight: "100%",
                        overflow: "auto",
                        padding: 3,
                    }}
                >
                    <SchemaDetailEditor
                        schema={schema}
                        selectedPath={selectedPath}
                        onSchemaUpdate={handleSchemaUpdate}
                        addPropertyPath={addPropertyPath}
                        onAddPropertyPathChange={setAddPropertyPath}
                        addItemPath={addItemPath}
                        onAddItemPathChange={setAddItemPath}
                        isPathLocked={isPathLocked}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default JsonSchemaEditor;
